// DataBronnen.js — beheerpagina voor de gegevensbestanden die handmatig worden
// ingelezen. De externe bronnen (MOW, Statbel, Provincies in Cijfers) weigeren
// verzoeken vanaf onze server met een 403, een captcha of een time-out, dus
// downloadt de gebruiker de bestanden zelf in zijn browser.
//
// Props:
//   onClose()  — terug naar het gemeentenoverzicht

import React, { useState, useEffect, useCallback } from 'react';
import { getBronnen, controleerBron, importeerBron, zetBronVersieTerug,
  getNetcongestieStand, stuurNetcongestieKaart, haalHuishoudensOp } from './api';
import NetcongestieKaartBron from './NetcongestieKaartBron';

const N = {
  ink:'#12161C', ink2:'#3D4652', ink3:'#6B7684', ink4:'#9AA4B1',
  paper:'#FFFFFF', paper2:'#FBFAF7', canvas:'#ECEAE5',
  line:'#D9D5CD', line2:'#C4BFB5', head:'#F6F4F0',
  accent:'#1F5C6B', accent2:'#2D7F92',
  ok:'#3F7D4E', okBg:'#EFF5EE', okBd:'#CFE0CB',
  warn:'#9C6420', warnBg:'#F9F1E4', warnBd:'#E8D3AC',
  red:'#B4442F', redBg:'#FBEEEB', redBd:'#E8C4BC',
};

// Waar de gebruiker het bestand kan downloaden. Deze pagina's zijn vanuit een
// browser wél bereikbaar; alleen onze server wordt geweerd.
// Rechtstreeks naar het bestand, niet naar een landingspagina met verwijzingen.
// Bij de kaartdienst van MOW moet er een aantal bij: zonder count levert hij
// standaard één record en denk je ten onrechte dat je alles hebt.
// De kaartdienst van MOW levert standaard JSON met de volledige vorm van elke
// sector erbij. Een browser toont JSON op het scherm in plaats van hem op te
// slaan, en die coördinaten maken het bestand tientallen megabytes groot
// terwijl we ze niet gebruiken. Daarom vragen we CSV met alleen de drie
// kolommen die we nodig hebben: dat wordt wél gedownload en is klein.
// De twee lagen hebben verschillende kolomnamen. Personenwagens zijn
// opgesplitst naar laadsnelheid en heten daarom b_normaal_2028; bestelwagens
// kennen die opsplitsing niet en heten b_2028. Vraag je de verkeerde naam op,
// dan weigert de dienst het hele verzoek.
const mowLink = (laag, kolommen) =>
  'https://geoserver.gis.cloud.mow.vlaanderen.be/geoserver/beleid/wfs' +
  '?service=WFS&version=2.0.0&request=GetFeature' +
  `&typeName=beleid:${laag}` +
  '&outputFormat=csv' +
  `&propertyName=${kolommen}` +
  '&count=50000';

// Bronnen waarvan de link naar een pagina wijst in plaats van naar een
// bestand, of waarvan de site automatisch ophalen weigert. Rechtstreeks
// ophalen kan daar niet, dus die knop blijft weg; in plaats daarvan staat er
// een knop om het bestand zelf te downloaden. De pagina van FOD Mobiliteit
// toont eerst een controlevraag of de bezoeker een mens is (HL, 28 oktober
// 2026), dus het voertuigenpark staat hier ook.
const NIET_OPHALEN = ['laadpunten', 'bevolking_sector', 'wagens_sector', 'voertuigenpark'];

const BRONLINKS = {
  // Rechtstreeks naar de pagina van MOW waar de uitvoer gekozen wordt, niet
  // naar de beschrijving in de catalogus van Metadata Vlaanderen (HL, 17
  // oktober 2026). Daar staat de keuze voor CSV, Lambert 2008-EPSG:3812.
  laadpunten: 'https://fme.gis.cloud.mow.vlaanderen.be/fmeserver/apps/ConvertLaadpunten',
  voertuigenpark: 'https://mobilit.belgium.be/nl/documenten-en-statistieken/open-data/wegverkeer/wagenpark',
  mow_m1: mowLink('m1_laadbehoeften', 'type,code,b_normaal_2028,b_normaal_2030'),
  // Bij bestelwagens ook de naam en de vorm van elke deelgemeente, in graden,
  // zodat een laadplein aan zijn deelgemeente gekoppeld kan worden. Daarom
  // hier GeoJSON in plaats van CSV; zonder deze twee vervalt de verdeling per
  // deelgemeente op tabblad Dashboard.
  mow_n1: 'https://geoserver.gis.cloud.mow.vlaanderen.be/geoserver/beleid/wfs' +
          '?service=WFS&version=2.0.0&request=GetFeature' +
          '&typeName=beleid:n1_laadbehoeften' +
          '&outputFormat=application/json&srsName=EPSG:4326' +
          '&propertyName=type,code,naam,b_2028,b_2030,geom' +
          '&count=50000',
  // Bevolking en auto's per statistische sector: naar de lijst "Statistische
  // sectoren" van Statbel (HL, 17 oktober 2026). Daar staan beide datasets,
  // de nieuwste jaargang bovenaan, zodat een nieuwe jaargang niet gemist wordt.
  // De vorige link (category=191, Geografische bestanden) bevatte de bevolking
  // per sector niet.
  bevolking_sector: 'https://statbel.fgov.be/nl/open-data?category=209',
  wagens_sector: 'https://statbel.fgov.be/nl/open-data?category=209',
  // Rechtstreeks naar het bestand. Provincies in Cijfers verplaatste beide
  // bestanden in september 2026 uit de map gemeente_statsec_wijken; deze
  // adressen staan in hun eigen readme onder Kernbestanden.
  ggw7_indeling: 'https://raw.githubusercontent.com/provinciesincijfers/gebiedsniveaus/master/data_voor_swing/aggregatietabellen/statsec_ggw7.xlsx',
  ggw7_namen: 'https://raw.githubusercontent.com/provinciesincijfers/gebiedsniveaus/master/data_voor_swing/gebiedsdefinities/ggw7.xlsx',
};

// Welk bestand er precies nodig is. Staat altijd in beeld, niet alleen als de
// bron nog ontbreekt, want de verkeerde variant kiezen is bij Statbel een
// makkelijke vergissing.
const WELK_BESTAND = {
  voertuigenpark:
    'Nodig: het bestand van FOD Mobiliteit met de jaarcijfers per gemeente, bestandsnaam '
    + 'End[jaartal]_DIV_PARK_OpenData_NIS.xlsx, niet het bestand per provincie. De gemeenten staan '
    + 'erin met hun Franse naam, Leuven als Louvain. Dit bestand dient alleen ter controle. Het telt '
    + 'de auto\u2019s op het adres waar ze zijn ingeschreven, dus ook de leasewagens op de zetel van een '
    + 'leasemaatschappij. Het aantal auto\u2019s van een gemeente komt uit het Statbel-bestand met de '
    + 'wagens per huishouden. Telt dit bestand meer dan 1,2 keer zoveel auto\u2019s als het '
    + 'Statbel-bestand, dan verschijnt in het bewerkscherm van de gemeente een waarschuwing dat er een '
    + 'leasevloot in de gemeente zit.',
  laadpunten:
    'Nodig: de volledige uitvoer van de laadpuntendataset van MOW. Open de pagina via de link '
    + 'hiernaast en download de CSV, Lambert 2008-EPSG:3812.',
  bevolking_sector:
    'Nodig: het Statbel-bestand "Bevolking per statistische sector", de variant met de NIEUWE '
    + 'sectorindeling, herkenbaar aan NEW in de bestandsnaam. De kaartvormen in deze toepassing '
    + 'staan sinds augustus 2026 op die indeling; de variant met OLD in de naam schrijft de '
    + 'sectorcodes anders en wordt bij het inlezen geweigerd.',
  wagens_sector:
    'Nodig: het Statbel-bestand "Aantal wagens per huishouden per statistische sector", '
    + 'meest recente jaargang, bestandsnaam TF_CAR_HH_SECTOR_JAARTAL.xlsx. Dit bestand bepaalt het '
    + 'aantal auto\u2019s van een gemeente en de verdeling daarvan over de wijken en sectoren. Het telt '
    + 'alleen auto\u2019s die bij een huishouden horen, dus niet de auto\u2019s op naam van een bedrijf of '
    + 'een instelling; voor het aantal voertuigen van een gemeente wordt daarvoor gecorrigeerd. De '
    + 'sectorcodes staan in de oude indeling van Statbel en worden bij het gebruik omgerekend naar '
    + 'de nieuwe.',
  ggw7_indeling:
    'Nodig: het bestand statsec_ggw7.xlsx van Provincies in Cijfers. Dit is de wijkindeling '
    + 'die de gemeenten zelf hebben vastgesteld: per statistische sector staat erin bij welke '
    + 'wijk hij hoort. Niet de versie met 2024 in de naam: dat is de oude indeling.',
  ggw7_namen:
    'Nodig: het bestand ggw7.xlsx van Provincies in Cijfers. Dat geeft de naam bij elke '
    + 'wijkcode. Ontbreekt dit bestand, dan gebruikt de app de namen die standaard met de '
    + 'toepassing meekomen. Niet de versie met 2024 in de naam: dat is de oude indeling.',
  huishoudens:
    'Nodig: het aantal private huishoudens per gemeente volgens het Rijksregister, op 1 januari '
    + 'van elk jaar. LaadAtlas haalt dit zelf op bij Provincies in Cijfers; een bestand inlezen is '
    + 'alleen nodig als dat niet lukt.',
};

// MOW gebruikt eigen aanduidingen voor het niveau; hier staan ze in gewone taal.
const NIVEAUNAAM = {
  nis9: 'statistische sectoren',
  nis6: 'deelgemeenten',
  nis: 'gemeenten',
  gemeente: 'gemeenten',
  deelgemeente: 'deelgemeenten',
  sector: 'statistische sectoren',
};

// Wat er misgaat als een bron ontbreekt. Concreter dan "geen gegevens".
const GEVOLG_ONTBREEKT = {
  laadpunten: 'Zolang deze niet is ingelezen rekent de app met de laadpunten die met de toepassing zijn meegeleverd, en die staan vast op 17 juli 2026.',
  voertuigenpark: 'Zonder deze gegevens ontbreekt de controle op leasevloten. Het aantal auto\u2019s van een gemeente verandert er niet door.',
  mow_m1: 'Zonder deze gegevens kan het laadplan de eigen prognose niet naast het minimum van MOW leggen.',
  mow_n1: 'Zonder deze gegevens blijft de regel over bestelwagens leeg in het laadplan.',
  bevolking_sector: 'Zonder deze gegevens is niet te zeggen hoeveel inwoners er buiten de dekking van een laadpunt wonen.',
  wagens_sector: 'Zonder deze gegevens wordt de behoefte per sector verdeeld naar inwonertal in plaats van naar het aantal auto\u2019s.',
  ggw7_indeling: 'Zonder deze gegevens gebruikt de app de wijkindeling die standaard meekomt.',
  ggw7_namen: 'Zonder deze gegevens gebruikt de app de wijknamen die standaard meekomen.',
  huishoudens: 'Zonder deze gegevens rekent de app elk jaar met hetzelfde aantal auto\u2019s.',
};

const datum = (d) => d ? new Date(d).toLocaleDateString('nl-BE', { day:'numeric', month:'long', year:'numeric' }) : '—';
const getal = (n) => (n ?? 0).toLocaleString('nl-BE');

export default function DataBronnen({ onClose }) {
  const [bronnen, setBronnen]   = useState([]);
  const [laden, setLaden]       = useState(true);
  const [fout, setFout]         = useState('');
  const [uitgeklapt, setUitgeklapt] = useState({});
  const [venster, setVenster]   = useState(null);   // { bron, naam, herkomst }

  const haalOp = useCallback(async () => {
    setLaden(true); setFout('');
    try {
      const d = await getBronnen();
      setBronnen(d.bronnen || []);
    } catch (e) {
      setFout(e.message);
    }
    setLaden(false);
  }, []);

  useEffect(() => { haalOp(); }, [haalOp]);

  const terugzetten = async (bron, importId) => {
    try { await zetBronVersieTerug(bron, importId); await haalOp(); }
    catch (e) { setFout(e.message); }
  };

  const s = stijlen;

  return (
    <div style={s.pagina}>
      <header style={s.topbar}>
        <div style={s.brand}>
          <div style={s.brandMark}>SL</div>
          <span style={s.brandTxt}>Strategisch Laadplan</span>
        </div>
        <button style={s.terug} onClick={onClose}>← Alle gemeenten</button>
      </header>

      <div style={s.kop}>
        <div style={s.eyebrow}>Beheer</div>
        <h1 style={s.h1}>Data bronnen</h1>
        <div style={s.lede}>
          Deze gegevens gelden voor alle gemeenten samen. Sommige haalt de app zelf op,
          andere lees je hier in met een bestand dat je bij de bron downloadt.
        </div>
      </div>

      <div style={s.body}>
        {fout && <div style={s.foutBalk}>{fout}</div>}

        <div style={s.sec}>Handmatig inlezen</div>

        {laden ? (
          <div style={s.leeg}>Gegevens ophalen…</div>
        ) : bronnen.map(b => {
          const kleur = b.status === 'actueel' ? 'ok' : b.status === 'verouderd' ? 'let' : 'mis';
          const open = uitgeklapt[b.bron];
          return (
            <div key={b.bron} style={s.kaart(kleur)}>
              <div style={s.ico(kleur)}>{kleur === 'ok' ? '✓' : kleur === 'let' ? '!' : '–'}</div>

              <div style={s.inh}>
                <div style={s.naam}>{b.naam}</div>
                <div style={s.herkomst}>{b.herkomst} · per {b.sleutelNaam}</div>
                {WELK_BESTAND[b.bron] && (
                  <div style={s.welkBestand}>{WELK_BESTAND[b.bron]}</div>
                )}

                {b.actief && (
                  <div style={s.rij}>
                    <span>Peildatum <strong style={s.sterk}>{datum(b.actief.peildatum)}</strong></span>
                    <span>Ingelezen <strong style={s.sterk}>{datum(b.actief.ingelezen_op)}</strong></span>
                    <span><strong style={s.sterk}>{getal(b.actief.aantal_records)}</strong> records</span>
                  </div>
                )}

                {b.status === 'verouderd' && (
                  <div style={s.mld('let')}>
                    {b.grensDagen != null
                      ? `Dit bestand is ${b.dagenOud} dagen oud. MOW vernieuwt de laadpunten maandelijks.`
                      : `Deze gegevens zijn ${b.maandenOud} maanden oud. Controleer bij de bron of er een nieuwere versie is.`}
                  </div>
                )}
                {b.status === 'ontbreekt' && (
                  <div style={s.mld('mis')}>
                    Nog niet ingelezen. {GEVOLG_ONTBREEKT[b.bron] || ''}
                  </div>
                )}

                {b.vorigeVersies?.length > 0 && (
                  <div style={{ marginTop:9 }}>
                    <button style={s.versKnop}
                      onClick={() => setUitgeklapt(v => ({ ...v, [b.bron]: !v[b.bron] }))}>
                      Vorige versies ({b.vorigeVersies.length}) {open ? '▴' : '▾'}
                    </button>
                    {open && (
                      <div style={s.versLijst}>
                        {b.vorigeVersies.map(v => (
                          <div key={v.id} style={s.versRij}>
                            <span>
                              Peildatum {datum(v.peildatum)} · ingelezen {datum(v.ingelezen_op)} · {getal(v.aantal_records)} records
                            </span>
                            <button style={s.knop('mini')} onClick={() => terugzetten(b.bron, v.id)}>
                              Terugzetten
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={s.akties}>
                <button
                  style={s.knop('eerste')}
                  onClick={() => setVenster({ bron: b.bron, naam: b.naam, herkomst: b.herkomst })}>
                  {b.status === 'ontbreekt' ? 'Inlezen' : b.status === 'verouderd' ? 'Bijwerken' : 'Nieuw bestand'}
                </button>
                {BRONLINKS[b.bron] && (
                  <a style={s.bronLink} href={BRONLINKS[b.bron]}
                     target="_blank" rel="noreferrer" download>
                    Bestand downloaden ↓
                  </a>
                )}
              </div>
            </div>
          );
        })}

        <NetcongestieKaartBron
          stijl={s}
          kleuren={N}
          stand={getNetcongestieStand}
          stuur={stuurNetcongestieKaart}
        />

        {/* Deze twee komen niet uit een bestand maar worden per gemeente bij een
            kaartdienst opgehaald, met de knoppen op het tabblad Plaatsing. Ze
            horen hier wel te staan: een gemeente wil weten waar de gegevens
            vandaan komen. De twee bronnen voor de status van laadpunten haalt
            de server elk uur zelf op, voor heel Vlaanderen tegelijk. */}
        <div style={s.sec}>Bronnen die de app zelf ophaalt</div>
        {[
          ['Adressen · Adressenregister Vlaanderen',
           'E\u00e9n punt per woning, appartement of winkel, alleen de adressen die in gebruik zijn. '
           + 'Bepaalt waar binnen een wijk de vraag naar laadpunten zit, en welk gebied op het '
           + 'tabblad Dekking als bewoond geldt.'],
          ['Wegen · OpenStreetMap',
           'De ligging van de straten, hun naam en het soort weg. Bepaalt naar welke straat een '
           + 'laadpaal wordt getrokken, en welke straatnaam in het plan komt te staan.'],
          ['Status van laadpunten · Eco-Movement',
           'De status van elk laadpunt van de deelnemende exploitanten, elk uur opgevraagd via '
           + 'transportdata.be, onder licentie CC BY 4.0. Bepaalt de bezetting op het tabblad Bezetting.'],
          ['Status van laadpunten · Road (E-Flux)',
           'Dezelfde gegevens voor de laadpunten van Road, elk uur opgevraagd via transportdata.be. '
           + 'Een laadpunt dat in beide bronnen staat, telt één keer.'],
        ].map(([naam, wat]) => (
          <div key={naam} style={s.auto}>
            <div>
              <div style={s.autoNaam}>{naam}</div>
              <div style={s.autoHoe}>{wat}</div>
            </div>
          </div>
        ))}

        <div style={s.sec}>Data inlezen via API</div>
        {[
          ['Private laadpunten',   'Fluvius open data · via de knop in de bewerkpagina'],
          ['Inwonertal',           'Rijksregister · via de knop in de bewerkpagina'],
        ].map(([naam, hoe]) => (
          <div key={naam} style={s.auto}>
            <div>
              <div style={s.autoNaam}>{naam}</div>
              <div style={s.autoHoe}>{hoe}</div>
            </div>
            <div style={s.badge}>werkt</div>
          </div>
        ))}
      </div>

      {venster && (
        <ImportVenster
          bron={venster.bron}
          naam={venster.naam}
          herkomst={venster.herkomst}
          onKlaar={() => { setVenster(null); haalOp(); }}
          onClose={() => setVenster(null)}
        />
      )}
    </div>
  );
}

// ── Importvenster ────────────────────────────────────────────────────────
function ImportVenster({ bron, naam, herkomst, onKlaar, onClose }) {
  const [stap, setStap]         = useState(1);
  const [bestand, setBestand]   = useState(null);
  const [controle, setControle] = useState(null);
  const [stappen, setStappen]   = useState([]);
  const [bezig, setBezig]       = useState(false);
  const [fout, setFout]         = useState('');
  const [klaar, setKlaar]       = useState(null);
  const [sleep, setSleep]       = useState(false);
  const [ophalen, setOphalen]   = useState(false);
  // De peildatum komt uit de bestandsnaam als die er staat; anders vult de
  // gebruiker hem in. Zonder peildatum weet de app niet hoe oud de gegevens
  // zijn en komt er ook geen datum in het laadplan.
  const [peildatum, setPeildatum] = useState('');

  const s = stijlen;
  // Bronnen zonder ophaalknop: het bestand wordt zelf gedownload en gesleept.
  const zelfDownloaden = Boolean(BRONLINKS[bron]) && NIET_OPHALEN.includes(bron);

  const kiesBestand = async (f) => {
    if (!f) return;
    setBestand(f); setFout(''); setBezig(true);
    try {
      const c = await controleerBron(bron, f);
      setControle(c);
      setPeildatum(c.peildatumVoorstel || '');
      setStap(2);
    } catch (e) {
      setFout(e.message);
    }
    setBezig(false);
  };

  // De browser van de gebruiker kan wél bij de bron; onze server niet. Dus
  // halen we het bestand hier op en sturen het meteen door. Weigert de bron
  // dat, dan valt de app terug op zelf downloaden en slepen.
  const haalOpBijBron = async () => {
    setFout(''); setBezig(true); setOphalen(true);
    try {
      const resp = await fetch(BRONLINKS[bron]);
      if (!resp.ok) throw new Error(`De bron antwoordde met foutcode ${resp.status}.`);
      const blob = await resp.blob();
      if (!blob.size) throw new Error('De bron gaf een leeg bestand terug.');
      // Wijst de link rechtstreeks naar een Excel-bestand, dan diens eigen naam.
      const laatsteDeel = BRONLINKS[bron].split('?')[0].split('/').pop();
      const naamUitLink = /\.xlsx$/i.test(laatsteDeel) ? laatsteDeel
        : BRONLINKS[bron].includes('outputFormat=csv') ? `${bron}.csv` : `${bron}.json`;
      // De soort van het opgehaalde bestand meegeven; zonder soort kwam het
      // bestand leeg aan bij de server.
      await kiesBestand(new File([blob], naamUitLink, { type: blob.type || 'application/octet-stream' }));
    } catch (e) {
      setFout(
        'Het is niet gelukt het bestand rechtstreeks op te halen: ' + e.message +
        ' Deze bron staat dat niet toe vanaf een andere website. Gebruik de knop ' +
        'hieronder om het bestand zelf te downloaden en sleep het daarna hierheen.'
      );
      setBezig(false);
    }
    setOphalen(false);
  };

  // De huishoudens haalt de server zelf op bij Provincies in Cijfers, met de
  // sleutel die alleen de server heeft. Lukt dat niet, dan blijft stap 1 open
  // en kan het bestand alsnog zelf worden ingelezen.
  const haalOpBijPinc = async () => {
    setFout(''); setBezig(true); setOphalen(true);
    try {
      const r = await haalHuishoudensOp();
      setStappen(r.stappen || []);
      setKlaar(r);
      setStap(3);
    } catch (e) {
      const reden = String(e.message || '').replace(/[.\s]+$/, '');
      setFout(`Ophalen bij Provincies in Cijfers is niet gelukt: ${reden}. Lees het bestand zelf in.`);
    }
    setBezig(false); setOphalen(false);
  };

  const lees = async () => {
    setStap(3); setBezig(true); setFout(''); setStappen([]);
    try {
      const r = await importeerBron(bron, bestand, peildatum || null);
      setStappen(r.stappen || []);
      setKlaar(r);
    } catch (e) {
      setStappen(e.stappen || []);
      setFout(e.message);
    }
    setBezig(false);
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && !bezig && onClose()}>
      <div style={s.venster}>
        <div style={s.vKop}>
          <div>
            <div style={s.vTitel}>{naam} inlezen</div>
            <div style={s.vSub}>{herkomst}</div>
          </div>
          <button style={s.sluit} onClick={onClose} title="Sluiten">✕</button>
        </div>

        <div style={s.vBody}>
          <div style={s.stappenBalk}>
            {['Bestand kiezen', 'Controleren', 'Inlezen'].map((t, i) => (
              <div key={t} style={s.stapVak(i + 1 === stap, i + 1 < stap)}>{i + 1} · {t}</div>
            ))}
          </div>

          {/* STAP 1 */}
          {stap === 1 && (
            <div>
              {WELK_BESTAND[bron] && (
                <div style={s.welkBestandVenster}>{WELK_BESTAND[bron]}</div>
              )}
              {bron === 'huishoudens' && (
                <>
                  <button style={s.haalKnop} onClick={haalOpBijPinc} disabled={bezig}>
                    {ophalen ? 'Bezig met ophalen bij de bron…' : 'Ophalen bij Provincies in Cijfers'}
                  </button>
                  <div style={s.ofRegel}>of kies zelf een bestand</div>
                </>
              )}
              {BRONLINKS[bron] && !NIET_OPHALEN.includes(bron) && (
                <>
                  <button style={s.haalKnop} onClick={haalOpBijBron} disabled={bezig}>
                    {ophalen ? 'Bezig met ophalen bij de bron…' : 'Ophalen bij de bron'}
                  </button>
                  <div style={s.ofRegel}>of kies zelf een bestand</div>
                </>
              )}
              {zelfDownloaden && (
                <a style={s.downloadKnop} href={BRONLINKS[bron]} target="_blank" rel="noreferrer">
                  Bestand downloaden bij de bron ↗
                </a>
              )}
              <label
                style={s.sleepVak(sleep)}
                onDragOver={e => { e.preventDefault(); setSleep(true); }}
                onDragLeave={() => setSleep(false)}
                onDrop={e => { e.preventDefault(); setSleep(false); kiesBestand(e.dataTransfer.files?.[0]); }}
              >
                <input type="file" style={{ display:'none' }}
                  accept=".xlsx,.xls,.xlsm,.csv,.json,.geojson,.xml,.gml,.txt"
                  onChange={e => kiesBestand(e.target.files?.[0])} />
                <div style={{ fontSize:30, color:N.ink4 }}>↑</div>
                <div style={s.sleepTitel}>
                  {bezig ? 'Bestand lezen, dit kan bij een groot bestand een minuut duren…'
                         : 'Sleep het bestand hierheen of klik om te kiezen'}
                </div>
                <div style={s.sleepSub}>Excel, CSV, GeoJSON of XML</div>
              </label>

              {fout && <div style={s.foutVak}>{fout}</div>}

              {fout && BRONLINKS[bron] && !zelfDownloaden && (
                <a style={s.terugvalKnop} href={BRONLINKS[bron]} target="_blank" rel="noreferrer">
                  Bestand zelf downloaden ↗
                </a>
              )}

              <div style={s.hint}>
                <strong style={{ color:N.accent }}>Hoe werkt dit?</strong><br />
                {zelfDownloaden ? (
                  <>Deze bron staat automatisch ophalen niet toe. Het bestand is te downloaden met
                  de knop hierboven en daarna hierheen te slepen. Excel, CSV, GeoJSON en XML kunnen
                  er allemaal in.</>
                ) : (<>De app haalt het bestand rechtstreeks bij de bron op. Lukt dat niet omdat de
                bron dat niet toestaat, dan download je het zelf via de knop die dan verschijnt
                en sleep je het hierheen. Excel, CSV, GeoJSON en XML kunnen er allemaal in.</>)}
              </div>
            </div>
          )}

          {/* STAP 2 */}
          {stap === 2 && controle && (
            <div>
              <div style={s.blokTitel}>Herkende kolommen</div>
              {Object.entries(controle.koppeling).map(([veld, kolom]) => (
                <div key={veld} style={s.kolomRij}>
                  <span><strong style={s.sterk}>{kolom}</strong> → {veld}</span>
                  <span style={s.kolomOk}>gekoppeld</span>
                </div>
              ))}
              {controle.ongebruikteKolommen.slice(0, 6).map(k => (
                <div key={k} style={s.kolomRij}>
                  <span><strong style={s.sterk}>{k}</strong></span>
                  <span style={s.kolomNiet}>niet gebruikt</span>
                </div>
              ))}

              <div style={s.blokTitel}>Eerste rijen</div>
              <table style={s.tabel}>
                <thead>
                  <tr>
                    <th style={s.th}>Sleutel</th>
                    {Object.keys(controle.voorbeeld[0]?.waarden || {}).map(k =>
                      <th key={k} style={{ ...s.th, textAlign:'right' }}>{k}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {controle.voorbeeld.map((v, i) => (
                    <tr key={i}>
                      <td style={s.td}>{v.sleutel}</td>
                      {Object.values(v.waarden).map((w, j) =>
                        <td key={j} style={{ ...s.td, textAlign:'right' }}>{w == null ? '—' : getal(w)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>

              {controle.afgekapt && (
                <div style={s.foutVak}>
                  Let op: dit bestand bevat {getal(controle.aantalRecords)} records, maar bij de
                  bron staan er {getal(controle.afgekapt)}. De download is afgekapt.
                  Gebruik de knop Bestand downloaden in het overzicht; die haalt alles op.
                </div>
              )}

              <div style={s.peilVak}>
                <label style={s.peilLabel}>
                  Peildatum van deze gegevens
                  {!controle.peildatumVoorstel && <span style={s.peilNodig}> · vul deze zelf in</span>}
                </label>
                <input
                  type="date"
                  style={s.peilInvoer(!peildatum)}
                  value={peildatum}
                  onChange={e => setPeildatum(e.target.value)}
                />
                <div style={s.peilUitleg}>
                  {controle.peildatumVoorstel
                    ? 'Overgenomen uit de bestandsnaam. Klopt dit niet, pas het dan aan.'
                    : 'Staat niet in de bestandsnaam. Zonder peildatum kan de app niet waarschuwen dat de gegevens verouderd zijn, en staat er geen datum bij in het laadplan.'}
                </div>
              </div>

              <div style={s.samen}>
                <div style={s.samenTitel}>Klaar om in te lezen</div>
                <div style={s.samenTekst}>
                  {controle.werkblad && (
                    <>Gelezen uit werkblad <strong style={s.sterk}>{controle.werkblad}</strong>
                    {controle.koprij > 1 && `, kolomkoppen op rij ${controle.koprij}`}.<br /></>
                  )}
                  {controle.perNiveau && (
                    <>Dit bestand bevat twee niveaus door elkaar:{' '}
                    {Object.entries(controle.perNiveau).map(([nv, aantal], i) => (
                      <span key={nv}>
                        {i > 0 && ' en '}
                        <strong style={s.sterk}>{getal(aantal)}</strong>{' '}
                        {NIVEAUNAAM[nv] || nv}
                      </span>
                    ))}. Die worden apart bewaard, zodat ze niet bij elkaar
                    worden opgeteld.<br /></>
                  )}
                  {controle.aantalBlokken > 1 && (
                    <>Dit bestand bevat <strong style={s.sterk}>{controle.aantalBlokken} jaargangen</strong> onder
                    elkaar. Alleen de meest recente is gebruikt
                    {controle.jaarUitBestand && `: ${controle.jaarUitBestand}`}.<br /></>
                  )}
                  {controle.samengevoegd && (
                    <>Dit bestand bevat <strong style={s.sterk}>
                    {getal(controle.samengevoegd.vanRijen)}</strong> losse regels, één per combinatie.
                    Die zijn samengevoegd tot <strong style={s.sterk}>
                    {getal(controle.samengevoegd.naarGebieden)}</strong> gebieden
                    {controle.samengevoegd.jaar && `, met alleen het jaar ${controle.samengevoegd.jaar}`}.<br /></>
                  )}
                  {controle.ontbrekendeVelden?.length > 0 && (
                    <>Dit bestand bevat geen{' '}
                    <strong style={s.sterk}>{controle.ontbrekendeVelden.join(', ')}</strong>.
                    {controle.ontbrekendeVelden.includes('elektrisch') &&
                      ' Het aandeel elektrische wagens komt er dus niet uit; dat blijft een schatting op basis van de welvaartsindex.'}
                    <br /></>
                  )}
                  {getal(controle.aantalRecords)} records gevonden
                  {controle.overgeslagen > 0 && `, ${getal(controle.overgeslagen)} overgeslagen zonder sleutel of waarden`}.<br />
                  Peildatum {peildatum ? datum(peildatum) : 'nog niet ingevuld'}.<br />
                  {controle.huidigeVersie
                    ? `De huidige gegevens van ${datum(controle.huidigeVersie.ingelezen_op)} worden bewaard als vorige versie.`
                    : 'Dit is de eerste inlezing voor deze bron.'}
                </div>
              </div>
            </div>
          )}

          {/* STAP 3 */}
          {stap === 3 && (
            <div>
              <div style={s.procKop}>
                <div style={s.blokTitel}>{bezig ? 'Bezig met inlezen' : fout ? 'Inlezen mislukt' : 'Inlezen voltooid'}</div>
              </div>
              <div style={s.balk}>
                <div style={s.balkVul(bezig ? 60 : 100, Boolean(fout))} />
              </div>

              <div style={s.log}>
                {stappen.length === 0 && <div style={{ color:N.ink4 }}>Bezig…</div>}
                {stappen.map((st, i) => (
                  <div key={i} style={s.logRij}>
                    <span style={s.logIcoon(st.soort)}>
                      {st.soort === 'ok' ? '✓' : st.soort === 'fout' ? '✕' : st.soort === 'let' ? '!' : '•'}
                    </span>
                    <span style={s.logTekst}>
                      <strong style={s.sterk}>{st.titel}</strong>{st.detail ? ` — ${st.detail}` : ''}
                    </span>
                    <span style={s.logTijd}>
                      {st.tijd ? new Date(st.tijd).toLocaleTimeString('nl-BE',
                        { hour:'2-digit', minute:'2-digit', second:'2-digit' }) : ''}
                    </span>
                  </div>
                ))}
              </div>

              {klaar && (
                <div style={s.resultaat(false)}>
                  <div style={s.resTitel(false)}>{getal(klaar.aantalRecords)} records ingelezen</div>
                  <div style={s.resTekst}>
                    Deze gegevens worden nu gebruikt in de app en in het laadplan.
                    {klaar.peildatum && ` Peildatum ${datum(klaar.peildatum)}.`}
                    {klaar.vorigeVersieBewaard && ' De vorige versie is bewaard en kan worden teruggezet.'}
                    {klaar.overgeslagen > 0 && ` ${getal(klaar.overgeslagen)} rijen zijn overgeslagen omdat ze geen sleutel of geen waarden hadden.`}
                  </div>
                </div>
              )}

              {fout && (
                <div style={s.resultaat(true)}>
                  <div style={s.resTitel(true)}>Er is niets gewijzigd</div>
                  <div style={s.resTekst}>
                    {fout}<br />
                    De vorige gegevens zijn ongewijzigd gebleven, dus je kunt gewoon verder werken.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={s.vVoet}>
          <div style={s.voetTekst}>
            {stap === 1 ? 'Kies een bestand om te beginnen'
              : stap === 2 ? 'Controleer of de kolommen goed herkend zijn'
              : bezig ? 'Even geduld, het bestand wordt verwerkt'
              : 'Klaar'}
          </div>
          <div style={{ display:'flex', gap:9 }}>
            <button style={s.knop('tweede')} onClick={onClose} disabled={bezig}>
              {stap === 3 && !bezig ? 'Sluiten' : 'Annuleren'}
            </button>
            {stap === 2 && (
              <button
                style={{ ...s.knop('eerste'), opacity: peildatum ? 1 : 0.5,
                         cursor: peildatum ? 'pointer' : 'not-allowed' }}
                onClick={lees}
                disabled={!peildatum}
                title={peildatum ? '' : 'Vul eerst de peildatum in'}>
                Inlezen
              </button>
            )}
            {stap === 3 && !bezig && !fout && (
              <button style={s.knop('eerste')} onClick={onKlaar}>Klaar</button>
            )}
            {stap === 3 && !bezig && fout && (
              <button style={s.knop('eerste')} onClick={() => { setStap(1); setFout(''); setBestand(null); setControle(null); }}>
                Ander bestand
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Stijlen ──────────────────────────────────────────────────────────────
const stijlen = {
  pagina:  { minHeight:'100vh', background:N.canvas, color:N.ink,
             fontFamily:"-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif" },
  topbar:  { background:N.ink, height:56, display:'flex', alignItems:'center', padding:'0 28px', gap:14 },
  brand:   { display:'flex', alignItems:'center', gap:10 },
  brandMark:{ width:26, height:26, borderRadius:6, background:N.accent2, display:'flex',
              alignItems:'center', justifyContent:'center', fontSize:12.5, fontWeight:800,
              color:'#fff', letterSpacing:'-0.03em' },
  brandTxt:{ fontSize:14.5, fontWeight:600, color:'#fff', letterSpacing:'-0.01em' },
  terug:   { marginLeft:12, fontSize:12.5, color:'rgba(255,255,255,0.55)', cursor:'pointer',
             padding:'5px 9px', borderRadius:6, background:'none', border:'none', fontFamily:'inherit' },

  kop:     { maxWidth:1000, margin:'0 auto', padding:'22px 26px 0' },
  eyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.13em', textTransform:'uppercase',
             color:N.accent, marginBottom:6 },
  h1:      { fontSize:26, fontWeight:700, letterSpacing:'-0.028em', marginBottom:7, color:N.ink },
  lede:    { fontSize:13.5, color:N.ink3, lineHeight:1.55, maxWidth:640 },
  body:    { maxWidth:1000, margin:'0 auto', padding:'18px 26px 50px' },
  sec:     { fontSize:11, fontWeight:700, letterSpacing:'0.09em', textTransform:'uppercase',
             color:N.ink4, margin:'22px 0 10px' },
  leeg:    { padding:'26px', textAlign:'center', color:N.ink4, fontSize:13.5 },
  foutBalk:{ background:N.redBg, border:`1px solid ${N.redBd}`, borderLeft:`3px solid ${N.red}`,
             borderRadius:9, padding:'11px 14px', fontSize:13, color:N.red, marginBottom:14 },

  kaart:   (k) => ({ background:N.paper, border:`1px solid ${N.line2}`,
             borderLeft:`3px solid ${k === 'ok' ? N.ok : k === 'let' ? N.warn : N.red}`,
             borderRadius:11, padding:'15px 18px', marginBottom:10,
             boxShadow:'0 1px 2px rgba(18,22,28,0.04)', display:'flex', gap:15, alignItems:'flex-start' }),
  ico:     (k) => ({ width:36, height:36, borderRadius:9, display:'flex', alignItems:'center',
             justifyContent:'center', flexShrink:0, fontSize:16, fontWeight:700,
             background: k === 'ok' ? N.okBg : k === 'let' ? N.warnBg : N.redBg,
             border: `1px solid ${k === 'ok' ? N.okBd : k === 'let' ? N.warnBd : N.redBd}`,
             color: k === 'ok' ? N.ok : k === 'let' ? N.warn : N.red }),
  inh:     { flex:1, minWidth:0 },
  naam:    { fontSize:15, fontWeight:700, letterSpacing:'-0.01em', marginBottom:3, color:N.ink },
  herkomst:{ fontSize:12.5, color:N.ink4, marginBottom:8 },
  welkBestand:{ fontSize:12.5, color:N.ink3, lineHeight:1.5, marginTop:-4, marginBottom:9,
                background:N.head, border:`1px solid ${N.line}`, borderRadius:7, padding:'8px 11px' },
  welkBestandVenster:{ fontSize:13, color:N.ink2, lineHeight:1.55, marginBottom:12,
                background:N.head, border:`1px solid ${N.line}`, borderRadius:7, padding:'10px 12px' },
  rij:     { display:'flex', gap:20, flexWrap:'wrap', fontSize:13, color:N.ink3 },
  sterk:   { color:N.ink, fontWeight:600 },
  mld:     (k) => ({ fontSize:12.5, padding:'8px 11px', borderRadius:7, lineHeight:1.5, marginTop:8,
             background: k === 'let' ? N.warnBg : N.redBg,
             border: `1px solid ${k === 'let' ? N.warnBd : N.redBd}`,
             color: k === 'let' ? N.warn : N.red }),
  versKnop:{ background:'none', border:'none', color:N.accent2, fontSize:12.5, fontWeight:600,
             padding:0, cursor:'pointer', fontFamily:'inherit' },
  versLijst:{ marginTop:8, borderTop:`1px solid ${N.line}`, paddingTop:8 },
  versRij: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0',
             fontSize:12.5, color:N.ink3, borderBottom:'1px solid #F1EEE8', gap:12 },
  akties:  { display:'flex', flexDirection:'column', gap:7, flexShrink:0, minWidth:148 },
  bronLink:{ color:N.accent2, fontSize:12.5, fontWeight:600, textAlign:'center',
             textDecoration:'none', padding:'2px' },

  auto:    { background:N.paper2, border:`1px dashed ${N.line2}`, borderRadius:11,
             padding:'13px 18px', marginBottom:8, display:'flex',
             justifyContent:'space-between', alignItems:'center' },
  autoNaam:{ fontSize:14, fontWeight:600, color:N.ink },
  autoHoe: { fontSize:12.5, color:N.ink4, marginTop:2 },
  badge:   { fontSize:11.5, fontWeight:700, color:N.ok, background:N.okBg,
             border:`1px solid ${N.okBd}`, padding:'3px 9px', borderRadius:5, whiteSpace:'nowrap' },

  knop:    (soort) => ({
             padding: soort === 'mini' ? '5px 10px' : '8px 14px',
             borderRadius:8, fontSize: soort === 'mini' ? 12 : 13,
             fontWeight: soort === 'eerste' ? 700 : 600, cursor:'pointer', fontFamily:'inherit',
             whiteSpace:'nowrap', textAlign:'center',
             border: soort === 'eerste' ? 'none' : `1px solid ${N.line2}`,
             background: soort === 'eerste' ? N.accent : N.paper,
             color: soort === 'eerste' ? '#fff' : N.ink2 }),

  overlay: { position:'fixed', inset:0, background:'rgba(18,22,28,0.45)', zIndex:2000,
             display:'flex', alignItems:'center', justifyContent:'center', padding:20 },
  venster: { width:640, maxHeight:'88vh', background:N.paper, border:`1px solid ${N.line2}`,
             borderRadius:14, display:'flex', flexDirection:'column', overflow:'hidden',
             boxShadow:'0 24px 60px rgba(18,22,28,0.24)' },
  vKop:    { padding:'16px 22px', borderBottom:`1px solid ${N.line}`,
             background:`linear-gradient(180deg,#FDFCFA,${N.paper})`, display:'flex',
             justifyContent:'space-between', alignItems:'flex-start', gap:16 },
  vTitel:  { fontSize:16, fontWeight:700, letterSpacing:'-0.015em', color:N.ink },
  vSub:    { fontSize:12.5, color:N.ink4, marginTop:2 },
  sluit:   { width:32, height:32, borderRadius:8, border:`1px solid ${N.line2}`, background:N.paper,
             color:N.ink2, cursor:'pointer', fontSize:17, lineHeight:1, display:'flex',
             alignItems:'center', justifyContent:'center', flexShrink:0, fontFamily:'inherit' },
  vBody:   { padding:'20px 22px', overflowY:'auto', flex:1, minHeight:290 },
  vVoet:   { padding:'13px 22px', borderTop:`1px solid ${N.line}`, background:N.head,
             display:'flex', justifyContent:'space-between', alignItems:'center', gap:14 },
  voetTekst:{ fontSize:12.5, color:N.ink3 },

  stappenBalk:{ display:'flex', gap:8, marginBottom:18 },
  stapVak: (nu, klaar) => ({ flex:1, padding:'8px 10px', borderRadius:8, fontSize:12.5,
             fontWeight:600, textAlign:'center',
             background: nu ? N.accent : klaar ? N.okBg : N.head,
             color: nu ? '#fff' : klaar ? N.ok : N.ink4,
             border: `1px solid ${nu ? N.accent : klaar ? N.okBd : N.line}` }),

  sleepVak:(actief) => ({ display:'block', border:`2px dashed ${actief ? N.accent2 : N.line2}`,
             borderRadius:11, padding:'30px 20px', textAlign:'center',
             background: actief ? '#F4F8F9' : N.paper2, cursor:'pointer' }),
  sleepTitel:{ fontSize:15, fontWeight:600, margin:'10px 0 5px', color:N.ink },
  sleepSub:{ fontSize:12.5, color:N.ink4, lineHeight:1.55 },
  hint:    { background:'#F2F7F8', border:'1px solid #D3E3E6', borderLeft:`3px solid ${N.accent2}`,
             borderRadius:9, padding:'12px 14px', fontSize:12.5, color:N.ink2,
             lineHeight:1.6, marginTop:15 },
  foutVak: { background:N.redBg, border:`1px solid ${N.redBd}`, borderLeft:`3px solid ${N.red}`,
             borderRadius:9, padding:'11px 14px', fontSize:12.5, color:N.red,
             marginTop:14, lineHeight:1.55 },

  haalKnop: { width:'100%', padding:'12px', borderRadius:9, fontSize:14, fontWeight:700,
              background:N.accent, color:'#fff', border:'none', cursor:'pointer',
              fontFamily:'inherit', marginBottom:12 },
  ofRegel:  { textAlign:'center', fontSize:12.5, color:N.ink4, marginBottom:12 },
  downloadKnop:{ display:'block', textAlign:'center', padding:'11px', borderRadius:9,
              fontSize:14, fontWeight:700, background:N.paper, color:N.accent,
              border:`1px solid ${N.accent}`, textDecoration:'none', marginBottom:12 },
  terugvalKnop:{ display:'block', textAlign:'center', padding:'10px', borderRadius:9,
              fontSize:13.5, fontWeight:700, background:N.paper, color:N.accent,
              border:`1px solid ${N.accent}`, textDecoration:'none', marginTop:12 },
  peilVak:  { background:N.head, border:`1px solid ${N.line}`, borderRadius:9,
              padding:'13px 15px', marginTop:15 },
  peilLabel:{ fontSize:12.5, fontWeight:600, color:N.ink2, display:'block', marginBottom:7 },
  peilNodig:{ color:N.warn, fontWeight:600 },
  peilInvoer:(leeg) => ({ padding:'8px 11px', borderRadius:7, fontSize:14,
              border:`1px solid ${leeg ? N.warnBd : N.line2}`,
              background: leeg ? N.warnBg : N.paper,
              color:N.ink, fontFamily:'inherit' }),
  peilUitleg:{ fontSize:11.5, color:N.ink4, lineHeight:1.5, marginTop:7 },
  blokTitel:{ fontSize:13.5, fontWeight:700, marginBottom:9, marginTop:2, color:N.ink },
  kolomRij:{ display:'flex', justifyContent:'space-between', alignItems:'center',
             padding:'9px 12px', background:N.paper2, border:`1px solid ${N.line}`,
             borderRadius:8, marginBottom:6, fontSize:13, color:N.ink2 },
  kolomOk: { fontSize:12, fontWeight:600, color:N.ok },
  kolomNiet:{ fontSize:12, fontWeight:600, color:N.ink4 },
  tabel:   { width:'100%', borderCollapse:'collapse', fontSize:13, marginBottom:4 },
  th:      { background:N.head, padding:'8px 11px', textAlign:'left', fontSize:11, fontWeight:700,
             textTransform:'uppercase', letterSpacing:'0.06em', color:N.ink4,
             borderBottom:`1px solid ${N.line}` },
  td:      { padding:'7px 11px', borderBottom:'1px solid #F1EEE8', color:N.ink2,
             fontVariantNumeric:'tabular-nums' },
  samen:   { background:N.okBg, border:`1px solid ${N.okBd}`, borderLeft:`3px solid ${N.ok}`,
             borderRadius:9, padding:'13px 15px', marginTop:15 },
  samenTitel:{ fontSize:13.5, fontWeight:700, color:N.ok, marginBottom:6 },
  samenTekst:{ fontSize:12.5, color:N.ink2, lineHeight:1.7 },

  procKop: { display:'flex', justifyContent:'space-between', alignItems:'baseline' },
  balk:    { height:8, background:N.head, border:`1px solid ${N.line}`, borderRadius:5,
             overflow:'hidden', margin:'6px 0 15px' },
  balkVul: (pct, fout) => ({ height:'100%', width:`${pct}%`,
             background: fout ? N.red : N.accent2, transition:'width 0.25s' }),
  log:     { background:'#FBFAF8', border:`1px solid ${N.line}`, borderRadius:9,
             padding:'12px 14px', maxHeight:210, overflowY:'auto', fontSize:12.5, lineHeight:1.85 },
  logRij:  { display:'flex', gap:9, alignItems:'flex-start', padding:'2px 0' },
  logIcoon:(soort) => ({ width:15, flexShrink:0, textAlign:'center', fontWeight:700,
             color: soort === 'ok' ? N.ok : soort === 'fout' ? N.red
                  : soort === 'let' ? N.warn : N.accent2 }),
  logTekst:{ color:N.ink2 },
  logTijd: { color:N.ink4, fontSize:11.5, marginLeft:'auto', flexShrink:0,
             fontVariantNumeric:'tabular-nums' },
  resultaat:(fout) => ({ borderRadius:9, padding:'14px 16px', marginTop:15,
             background: fout ? N.redBg : N.okBg,
             border: `1px solid ${fout ? N.redBd : N.okBd}`,
             borderLeft: `3px solid ${fout ? N.red : N.ok}` }),
  resTitel:(fout) => ({ fontSize:14, fontWeight:700, marginBottom:6, color: fout ? N.red : N.ok }),
  resTekst:{ fontSize:12.5, color:N.ink2, lineHeight:1.7 },
};
